import React, { useState, useCallback, useContext } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import Alert, { AlertButton, AlertBar }
  from '@fuww/library/src/Alert';
import LoadingIcon from '@fuww/library/src/Icons/Loading';
import { useIntl } from 'react-intl';
import { ApolloError, useMutation } from '@apollo/client';
import withQuery from '../../lib/withQuery';
import messages from '../../lib/messages.json';
import { Link } from '../../routes.mjs';
import { UserContext } from '../UserProvider';
import Loader from '../Loader';
import createMarketAccessRequest
  from '../../lib/mutations/createMarketAccessRequest.graphql';
import hiddenProductsExistsQuery
  from '../../lib/queries/products/hiddenProductsExistQuery.graphql';
import hasMarketAccessRequestQuery
  from '../../lib/queries/products/hasMarketAccessRequestQuery.graphql';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const moreProductsAvailableWithMarketAccessRequestPropertyTypes = {
  data: PropTypes.shape({
    hasMarketAccessRequest: PropTypes.bool,
  }).isRequired,
  marketId: PropTypes.number.isRequired,
};

type MoreProductsAvailableWithMarketAccessRequestProperties = InferProps<
  typeof moreProductsAvailableWithMarketAccessRequestPropertyTypes
>;

const MoreProductsAvailableWithMarketAccessRequest = ({
  data: { hasMarketAccessRequest },
  marketId,
}: MoreProductsAvailableWithMarketAccessRequestProperties) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApolloError | boolean>(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [
    createAccessRequest,
  ] = useMutation(createMarketAccessRequest, {
    onCompleted: () => {
      setLoading(false);
      setRequestSuccess(true);
    },
    onError: (graphqlError) => {
      setLoading(false);
      setError(graphqlError);
    },
  });
  const clickHandler = useCallback(() => {
    setLoading(true);
    createAccessRequest({
      variables: { marketId: Number(marketId) },
    });
  }, [marketId, createAccessRequest, setLoading]);

  if (error) {
    return (
      <AlertBar>
        <Alert
          severity="error"
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages.errorWhileRequestingAccess,
              ),
            }}
          />
        </Alert>
      </AlertBar>
    );
  }

  if (requestSuccess) {
    return (
      <AlertBar>
        <Alert
          severity="success"
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages.moreProductsAvailableSuccess,
              ),
            }}
          />
        </Alert>
      </AlertBar>
    );
  }

  if (hasMarketAccessRequest) {
    return (
      <AlertBar>
        <Alert
          severity="success"
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages.moreProductsAvailablePending,
              ),
            }}
          />
        </Alert>
      </AlertBar>
    );
  }

  return (
    <AlertBar>
      <Alert
        severity="warning"
        action={
          loading ? (
            <AlertButton disabled>
              <LoadingIcon title={intl.formatMessage(messages.loading)} />
            </AlertButton>
          ) : (
            <AlertButton
              className="request-buyer-access"
              onClick={() => {
                clickHandler();
              }}
            >
              {intl.formatMessage(messages.requestAccess)}
            </AlertButton>
          )
        }
      >
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              messages.moreProductsAvailable,
            ),
          }}
        />
      </Alert>
    </AlertBar>
  );
};

const MoreProductsAvailableWithMarketAccessRequestWithQuery = withQuery(
  MoreProductsAvailableWithMarketAccessRequest,
);

const moreProductsAvailableContentPropertyTypes = {
  data: PropTypes.shape({
    hiddenProductsExist: PropTypes.bool,
  }).isRequired,
  variables: PropTypes.shape({
    marketId: PropTypes.number.isRequired,
  }).isRequired,
};

type MoreProductsAvailableContentProperties = InferProps<
  typeof moreProductsAvailableContentPropertyTypes
>;

export const MoreProductsAvailableContent = ({
  data,
  variables: { marketId },
}: MoreProductsAvailableContentProperties) => {
  const { user, loading: userLoading } = useContext(UserContext);
  const intl = useIntl();
  const { hiddenProductsExist } = data;

  if (!hiddenProductsExist) {
    return null;
  }

  if (userLoading) {
    return <Loader fullScreen />;
  }

  if (!user) {
    return (
      <AlertBar>
        <Alert
          severity="warning"
          action={(
            <>
              <Link route="login" passHref>
                <AlertButton href="" className="hidden-products-login">
                  {intl.formatMessage(messages.login)}
                </AlertButton>
              </Link>
              |
              <Link route="register-buyer" passHref>
                <AlertButton href="" className="hidden-products-register">
                  {intl.formatMessage(messages.register)}
                </AlertButton>
              </Link>
            </>
          )}
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages.moreProductsAvailableAfterLogin,
              ),
            }}
          />
        </Alert>
      </AlertBar>
    );
  }

  if (!user.hasCompleteBuyerProfile) {
    return (
      <AlertBar>
        <Alert
          severity="warning"
          action={(
            <Link
              route="edit-user-profile"
              params={{ market: `${marketId}` }}
              passHref
            >
              <AlertButton href="" className="complete-buyer-profile">
                {intl.formatMessage(messages.updateProfile)}
              </AlertButton>
            </Link>
          )}
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages.moreProductsAvailableAfterUpdate,
              ),
            }}
          />
        </Alert>
      </AlertBar>
    );
  }

  return (
    <MoreProductsAvailableWithMarketAccessRequestWithQuery
      marketId={marketId}
      query={hasMarketAccessRequestQuery}
      variables={{ marketId }}
      errorMessage="Error loading market access requests."
    />
  );
};

// eslint-disable-next-line @stylistic/max-len
MoreProductsAvailableContent.propTypes = moreProductsAvailableContentPropertyTypes;

const MoreProductsAvailableWithQuery = withQuery(MoreProductsAvailableContent);

const moreProductsAvailablePropertyTypes = {
  variables: PropTypes.shape({
    marketId: PropTypes.number.isRequired,
    standId: PropTypes.number,
    locales: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

type MoreProductsAvailableProperties = InferProps<
  typeof moreProductsAvailablePropertyTypes
>;

const MoreProductsAvailable = ({
  variables,
}: MoreProductsAvailableProperties) => (
  <MoreProductsAvailableWithQuery
    query={hiddenProductsExistsQuery}
    variables={variables}
    errorMessage="Error loading products."
    loader={<Loader fullScreen />}
  />
);

MoreProductsAvailable.propTypes = moreProductsAvailablePropertyTypes;

export default MoreProductsAvailable;
